import React from 'react';
import useFetch, { CachePolicies } from 'use-http';
import Paginate from 'components/UI/Paginate';
import usePaginate from 'hooks/usePaginate';
import Table, { TableFilter } from 'components/UI/Table';
import { IBreach } from 'types/breach';
import Select from 'components/UI/Select';
import { useUnit } from 'effector-react';
import { $store, $storeStr } from 'models/store';
import { $user } from 'models/user';
import ZoomComponent from 'components/UI/Zoom';
import { getSortImage } from './utils';
import { PER_PAGE } from './common/constants';
import { createSortHandler, buildQueryParams, getStoreIdForDataAccess } from './common/tableUtils';
import { getFileUrl } from 'utils/fileUrl';
import { useStoresAndUsers } from './common/hooks/useStoresAndUsers';
import { useFilterInitialization } from './common/hooks/useFilterInitialization';
import { useFilterPagination } from './common/hooks/useFilterPagination';
import { createNameColumn } from './common/tableColumns';
import {
	createDateFilterFunction,
	createTimeFormatter,
	createBreachManagerFormatter,
	createManagerFilterFunction,
} from './common/formatters';
import { createPhotoViolationsExcelExport } from './common/excelExportUtils';
import { formatServiceTypeTitle } from 'utils/helpers';

export default function PhotoViolations() {
	const store = useUnit($store);
	const user = useUnit($user);
	const workList = useUnit($storeStr);
	const { get, loading, error } = useFetch({ cachePolicy: CachePolicies.NO_CACHE });

	const [data, setData] = React.useState<any[]>([]);
	const { filter: initialFilter, setFilter: setInitialFilter } = useFilterInitialization(user);
	const filter: TableFilter = React.useMemo(() => ({ ...initialFilter, type: 'PhotoError' }), [initialFilter]);
	const setFilter = React.useCallback((newFilter: TableFilter) => {
		const { type, ...filterWithoutType } = newFilter;
		setInitialFilter(filterWithoutType);
	}, [setInitialFilter]);
	const { pageCount, offset, setCount, handlePageClick, resetOffset, currentPage } = usePaginate(PER_PAGE);

	const { userList, isLoadingUsers, storeIdOptions, regionalManagersOptions, aoManagersOptions, isFilterLocked, loadUser } =
		useStoresAndUsers(filter);

	const sortTable = createSortHandler(filter, setFilter);

	useFilterPagination(filter, resetOffset);

	async function load() {
		let url = `/api/breach?offset=${offset}&limit=${PER_PAGE}`;
		const params = buildQueryParams(filter);
		const currentParams = new URLSearchParams(params);
		const storeId = getStoreIdForDataAccess(user, store);

		if (storeId !== null) {
			if (storeId) {
				url += `&storeId=${storeId}`;
			}
			url += `&${currentParams.toString()}`;

			const result = await get(url);
			if (result?.rows) {
				const sortedData = [...result.rows].sort((a: any, b: any) => {
					const numberA = a.checkin?.store?.number || 0;
					const numberB = b.checkin?.store?.number || 0;
					return numberA - numberB;
				});
				setData(sortedData);
				setCount(result.count);
			} else {
				setData([]);
				setCount(0);
			}
		} else {
			setData([]);
			setCount(0);
		}
	}

	React.useEffect(() => {
		load();
	}, [offset, PER_PAGE, filter, store]);

	const headers = [
		{
			value: 'number',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Номер магазина</span>
					<button onClick={() => sortTable('checkin.store.number')}>
						{getSortImage('checkin.store.number', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '150px',
			formatted: React.useCallback((row: IBreach) => {
				return <div>{row?.checkin?.store?.number}</div>;
			}, []),
			filter: React.useCallback(() => {
				const handleChange = (newValue: string): void => {
					const newFilter = { ...filter, storeId: newValue, userId: null };
					setFilter(newFilter);
				};

				const defVal = !filter.storeId && storeIdOptions.length === 1 ? storeIdOptions[0].value : undefined;

				return (
					<Select
						onChange={handleChange}
						options={storeIdOptions}
						isClearable
						isDisabled={isFilterLocked}
						defaultValue={defVal}
						menuHeight="600px"
					/>
				);
			}, [filter, setFilter, storeIdOptions, isFilterLocked]),
		},
		{
			value: 'title',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Название магазина</span>
					<button onClick={() => sortTable('checkin.store.title')}>
						{getSortImage('checkin.store.title', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '300px',
			formatted: React.useCallback((row: IBreach) => {
				return <div>{row?.checkin?.store?.title}</div>;
			}, []),
		},
		{
			value: 'position',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Тип услуги</span>
					<button onClick={() => sortTable('user.position')}>
						{getSortImage('user.position', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			formatted: React.useCallback((row: any) => {
				const position = row.user?.position ?? row.user?.outsourcing?.pos?.title;
				return <div>{formatServiceTypeTitle(position)}</div>;
			}, []),
		},
		createNameColumn(filter, setFilter, userList, loadUser, isFilterLocked, sortTable, isLoadingUsers),
		{
			value: 'time',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Время в магазине</span>
					<button onClick={() => sortTable('createdAt')}>
						{getSortImage('createdAt', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '300px',
			formatted: createTimeFormatter(false),
			filter: createDateFilterFunction(filter, setFilter),
		},
		{
			value: 'time_to_msk',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Время в магазине по МСК</span>
				</div>
			),
			width: '300px',
			formatted: createTimeFormatter(true),
			filter: createPhotoViolationsExcelExport(filter, user, store),
		},
		{
			value: 'photo',
			title: 'Фото',
			width: '200px',
			formatted: React.useCallback((row: IBreach) => {
				if (row.type !== 'PhotoError') return null;
				const file = row.checkin?.file;
				if (!file) return null;
				return (
					<div>
						<ZoomComponent src={getFileUrl(file)} />
					</div>
				);
			}, []),
		},
		{
			value: 'regional-director',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Закрепленный РД</span>
					<button onClick={() => sortTable('checkin.store.regional_manager')}>
						{getSortImage('checkin.store.regional_manager', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '150px',
			formatted: createBreachManagerFormatter('regional_manager'),
			filter: createManagerFilterFunction(filter, setFilter, regionalManagersOptions, 'regional_manager'),
			visible:
				!workList.includes('Розница') &&
				(workList.includes('Региональный директор') || workList.includes('Заместитель регионального директора')),
		},
		{
			value: 'administrative-department',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Закрепленный АО</span>
					<button onClick={() => sortTable('checkin.store.ao_manager')}>
						{getSortImage('checkin.store.ao_manager', filter.sortKey, filter.sortDirection)}
					</button>
				</div>
			),
			width: '150px',
			formatted: createBreachManagerFormatter('ao_manager'),
			filter: createManagerFilterFunction(filter, setFilter, aoManagersOptions, 'ao_manager'),
			visible: !workList.includes('Розница') && workList.includes('Начальник отдела'),
		},
	];

	return (
		<div>
			{error && 'Error!'}
			<Table headers={headers} rows={data} isLoading={loading} />
			{pageCount > 1 && <Paginate handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />}
		</div>
	);
}
